import $ from 'jquery'
import _ from 'lodash'
import BootslideMenu from 'scoreboard/components/bootslide/bootslide-menu'
import i18n from 'scoreboard/components/i18n'
import DatasetFieldSelector from 'scoreboard/components/bootslide/dataset-field-selector'
import menuIconOption from 'scoreboard/components/bootslide/menu-icon-option'
import widgetMetadata from '../widgets/widget-metadata'
import appDisplay from 'scoreboard/components/app-display'
import arrayToHash from 'scoreboard/components/array-to-hash'
import spotlight from 'scoreboard/components/spotlight'
import user from 'scoreboard/components/user'
import { datasetFieldFilter } from '../dataset-field-filter'
import SimpleDatasetSelector from 'scoreboard/components/bootslide/simple-dataset-selector'
import { getSelectableDatasets, findLinkField }
	from 'scoreboard/forms/dataset-field-form-util'
import WidgetState from 'scoreboard/forms/model/widget-state'
const { EDITABLE, CLICKABLE, VISIBLE } = WidgetState
import FormWidgetType from 'scoreboard/forms/model/form-widget-type'
import {getDialogDatasetOrDataset} from './pages-edit'
const {
	FIELD, BUTTON, CHECKBOXES,
	RECORD_LIST, TEXT, DASHBOARD
} = FormWidgetType

const AddFormWidgetMenu = BootslideMenu.extend({

	/** @type {Form} */
	model: null,

	/**
	 * Menu for adding widgets to pages within a form.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model
	 *
	 * @param {jQuery|Element} options.formEl the DOM element containing the
	 * previews of the widgets being edited
	 *
	 * @param {boolean=true} [options.showDashboardMenu] whether to show the dashboard
	 * menu option
	 *
	 * @param {boolean=true} [options.showRecordListMenu] whether to show the record
	 * list menu option
	 */
	initialize: function (options) {
		BootslideMenu.prototype.initialize.apply(this, arguments)
		this.options = options
		this.showDashboardMenu = options?.showDashboardMenu ?? true
		this.showRecordListMenu = options?.showRecordListMenu ?? true
		this.formEl = $(options.formEl)
		this.dataset = getDialogDatasetOrDataset(this.model)
	},

	menu: function () {
		const selectableDatasets = getSelectableDatasets(this.model)
		this.singleDataset = selectableDatasets.length == 1
		const firstMenu = this.singleDataset
			? this.fieldMenu()
			: this.datasetMenu(selectableDatasets)
		let targets = [
			firstMenu,

			// checkbox widget editor
			this.openEditor(CHECKBOXES, 'checkbox-widget'),

			// text widget editor
			this.openEditor(TEXT, 'add-widget-text'),

			// button widget
			this.buttonMenu()
		]

		if (this.showDashboardMenu) {
			// dashboard widget. Goes before record-list. #58389
			targets.push(this.dashboardMenu())
		}

		if (this.showRecordListMenu) {
			// record list editor
			targets.push(this.openEditor(RECORD_LIST, 'record-list'))
		}

		const menu = {
			label: i18n.get('dashboards.addWidget'),
			target: targets,
			layout: 'tiles'
		}

		return menu
	},

	datasetMenu: function(datasets) {
		const datasetList = new SimpleDatasetSelector({ datasets, initialDatasetId: this.dataset.id })
		this.childViews.push(datasetList)
		const bootslideMenu = this
		datasetList.on('select', ({ dataset }) => {
			bootslideMenu.bootslide.navigate('field-selector', { selected: dataset })
		})
		return _.extend(tileOption('datasets.field.single', 'forms-field-widget'), {
			label: i18n.get('datasets.dataset'),
			contentEndpoint: () => {
				datasetList.render()
				return datasetList.el
			},
			target: [ this.fieldMenu() ]
		})
	},

	fieldMenu: function () {
		const fieldListFilter = datasetFieldFilter(() =>
			arrayToHash(_(this.model.get('widgets'))
				.map('fieldId')
				.compact()
				.value())
		, false)
		this.fieldSelector = new DatasetFieldSelector({
			allowTotal: false,
			fieldListFilter,
			noFieldsMessage: this.noFieldsMessage,
			allowVisibleLinkedFields: true,
			fieldListSort: this.sortFields.bind(this)
		})

		this.childViews.push(this.fieldSelector)

		return _.extend(tileOption('datasets.field.single', 'forms-field-widget'), {
			id: 'field-selector',
			contentEndpoint: () => {
				this.fieldSelector.render()
				if (this.singleDataset) {
					this._navigateToFieldMenu({})
				}
				return this.fieldSelector.el
			},
			onNavigate: this._navigateToFieldMenu.bind(this)
		})
	},

	_navigateToFieldMenu: function ({ selected }) {
		// If dataset is null that means the user didn't have to select
		// one, so use the root one
		const dataset = selected || this.dataset
		// Remove any existing event listeners so it only fires once
		this.fieldSelector.off('select')
		this.fieldSelector.on('select', selection => {
			this._createWidgetConfig(selection, dataset)
			// A dataset field can only be used once on a page
			if (this.fieldSelector.fieldList) {
				this.fieldSelector.fieldList.removeField(selection.fieldId)
			}
		})
		this.fieldSelector.setDataset(dataset)

		// Update the menu label to reflect whether we're selecting a field
		// in the dataset or a field label from a linked dataset
		const labelI18nCode = dataset.id == this.dataset.id
			? 'datasets.field.single' : 'forms.valueLabelField'
		this.bootslide.setMenuLabel('field-selector',
			i18n.get(labelI18nCode))
	},

	_createWidgetConfig: function(selectedField, dataset) {
		const widgetConfig = {
			type: FIELD,
			suggestionType: 'none',
			inputValidationType: 'input-optional',
			allowTyping: true,
			state: getDefaultWidgetState(FIELD)
		}
		let field = selectedField.field
		let label = field.label
		if (field.datasetId != this.dataset.id) {
			// We are picking a field from a linked dataset, so we
			// want to set this up by looking for a dataset-link field
			// on the main dataset that links to the selected field's
			// dataset
			field = findLinkField(this.model, dataset)
			widgetConfig.valueLabelFieldId = selectedField.fieldId

			// Check if the dataset has a linked field to the main dataset
			if (_.find(dataset.get('fields'), f => f.linkedDatasetId === this.dataset.id)) {
				label = dataset.get('label')
			}
		}

		widgetConfig.label = label
		widgetConfig.fieldId = field.id
		this.addWidget(widgetConfig)
	},

	/**
	 * #41273 Sort the fields so the main dataset's fields are first
	 */
	sortFields: function(fields) {
		const thisDatasetId = this.dataset.get('id')
		return _.sortBy(fields, [
			f => f.datasetId === thisDatasetId ? 0 : f.datasetId,
			f => f.label
		])
	},

	noFieldsMessage: function (dataset) {
		dataset = dataset || this.dataset
		let url = 'datasets/' + dataset.id
		let label = _.escape(dataset.get('label'))

		let link = `<a href="${url}" class="sp-navigate text-link is-inlined">${label}</a>`

		return i18n.get('forms.widgets.noEditableDatasetFields', link)
	},

	buttonMenu: function () {
		return _.extend(tileOption('forms.widgets.widget.button', 'forms-button'), {
			target: () => {
				this.addWidget({
					type: BUTTON,
					buttonLabel: i18n.get('forms.widgets.widget.button'),
					state: 'clickable'
				})
			}
		})
	},

	dashboardMenu: function () {
		return this.openEditor(DASHBOARD, 'dashboard-form-widget', widget => {
			widget.dashboard = {
				// The DashboardType (as opposed to 'folder')
				type: DASHBOARD,
				background: 'white',
				snapType: user.model.get('dashboardSnapType'),
				// This indicates the "style" of the dashboard (as opposed to
				// 'strategyMaps'. It can also be used to determine whether a
				// dashboard belongs in tree for the Dashboards or Strategy Maps
				// section, but in this case, we need it because it gets passed
				// to widgets as an option (see ShapeModel.initialize())
				section: 'dashboards',
				widgets: []
			}
		})
	},

	openEditor: function (type, icon, widgetInitializer) {
		return _.extend(tileOption(`forms.widgets.widget.${type}`, icon), {
			target: () => {
				let widget = { type, state: getDefaultWidgetState(type) }
				widgetInitializer?.(widget)

				let constructor = widgetMetadata.editors[type]
				let editor = new constructor({
					model: this.model,
					widget
				})
				appDisplay.dialog(editor)
				editor.on('done', widget => this.addWidget(widget))
			}
		})
	},

	addWidget: function (widget) {
		widget.tempId = _.uniqueId('w')
		this.trigger('add-widget', widget)
		this.previewSelection()
	},

	previewSelection: function() {
		const lastWidget = _.last(this.model.get('widgets'))
		if (!lastWidget.valueLabelFieldId) {
			// temporarily hide the bootslide menu tooltip
			// #46722 if we selected a linked dataset field, `pages-edit` is
			// going to close the menu anyway, so don't do the preview
			BootslideMenu.prototype.previewSelection.apply(this, arguments)
		}

		// scroll down to the newly added widget and spotlight it
		let widgetEl = this.formEl.find('.form-widget').last()[0]
		// Using Scrolltop insteade of scrollIntoView() because the latter prevents
		// the menu from reopening
		this.formEl.scrollTop(widgetEl?.offsetTop)
		spotlight(widgetEl)
	}

})

function tileOption (label, iconId) {
	return {
		label: i18n.get(label),
		content: menuIconOption(iconId, label)
	}
}

function getDefaultWidgetState(type) {
	if (_.includes([FIELD, CHECKBOXES], type))
		return EDITABLE
	if (type === BUTTON) return CLICKABLE

	// for record-list, text, dashboard
	return VISIBLE
}

export default AddFormWidgetMenu
export { getDefaultWidgetState }

AddFormWidgetMenu.__filename__ = 'forms/edit/add-form-widget-menu.js'
